import React, { useEffect, useMemo, useState } from "react";
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  type Transition,
  type Variants,
} from "framer-motion";
import GoogleLoginButton from "../components/auth/GoogleLoginButton";
import AppleLoginButton from "../components/auth/AppleLoginButton";
import { login, getMe } from "../api/endpoints";
import { getOutdatedCompliances, type ComplianceStatus } from "../api/compliance";
import { LoginAuthError, AuthGateError, setAuthToken } from "../api/config";
import { useOptionalUser } from "../contexts/UserContext";
import { navigateTo } from "../utils/navigation";
import { EMAIL_RE, EMAIL_HTML_PATTERN, PASS_RE } from "../utils/regex";
import ComplianceModal from "../components/ComplianceModal";

// Motion easing matches brand token --ease: cubic-bezier(.22,.61,.36,1)
const EASE = [0.22, 0.61, 0.36, 1] as const;

const LOGINS_DISABLED_MESSAGE =
  "Sign-in is temporarily unavailable. Please try again shortly.";

export default function LoginPage(): React.JSX.Element {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [serverError, setServerError] = useState("");
  const [touched, setTouched] = useState({ email: false, password: false });
  const [submitting, setSubmitting] = useState(false);
  const [showComplianceModal, setShowComplianceModal] = useState(false);
  const [outdatedCompliances, setOutdatedCompliances] = useState<ComplianceStatus[]>([]);

  // Fail OPEN in the UI: if the config fetch failed, hasn't landed yet, or
  // the field is missing (older backend), treat logins as enabled. This is
  // presentation only — the server is the real, fail-closed gate and will
  // still reject with 403 logins_disabled if this client-side read is stale.
  const userCtx = useOptionalUser();
  const loginEnabled = userCtx?.loginEnabled ?? true;

  const emailTrimmed = email.trim();
  const emailValid = EMAIL_RE.test(emailTrimmed);
  const passwordValid = PASS_RE.test(password);

  useEffect(() => {
    if (typeof window !== "undefined") {
      const isBanned = new URLSearchParams(window.location.search).get("banned");
      if (isBanned === "true") {
        setServerError("Your account has been banned. Please contact support for more information.");
      }
    }
  }, []);

  // Allow only same-origin non-auth paths in ?next=
  const nextSafe = useMemo(() => {
    if (typeof window === "undefined") return null;
    const raw = new URLSearchParams(window.location.search).get("next");
    if (!raw) return null;
    const forbidden = ["/login", "/register", "/forgot-password", "/email-verification"];
    const isForbidden = forbidden.some((p) => raw === p || raw.startsWith(p + "?"));
    if (raw.startsWith("/") && !raw.startsWith("//") && !isForbidden) return raw;
    return null;
  }, []);

  const prefersReduced = useReducedMotion();
  const [showIntro, setShowIntro] = useState<boolean>(() => !prefersReduced);

  useEffect(() => {
    if (prefersReduced) { setShowIntro(false); return; }
    const t = setTimeout(() => { setShowIntro(false); }, 1000);
    return () => { clearTimeout(t); };
  }, [prefersReduced]);

  // --t-base: 320ms  --t-slow: 640ms
  const spring: Transition = prefersReduced
    ? { duration: 0 }
    : { type: "spring", stiffness: 420, damping: 36, mass: 0.9 };

  const fadeQuick: Transition = prefersReduced
    ? { duration: 0 }
    : { duration: 0.32, ease: EASE };

  const cardVariants: Variants = {
    hidden: { opacity: 0, y: 10 },
    visible: { opacity: 1, y: 0, transition: spring },
  };

  const brandRowVariants: Variants = {
    hidden: { opacity: 0, y: -6 },
    visible: { opacity: 1, y: 0, transition: spring },
  };

  const showEmailError = touched.email && !emailValid && emailTrimmed.length > 0;
  const showPasswordError = touched.password && !passwordValid && password.length > 0;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setServerError("");
    setTouched({ email: true, password: true });
    if (!emailValid || !passwordValid) return;

    setSubmitting(true);
    try {
      const res = await login(emailTrimmed, password);
      setAuthToken(res["jwt-key"]);

      const me = await getMe();
      const nameOk = me.name && me.name.trim() !== "";
      const surnameOk = me.surname && me.surname.trim() !== "";

      try {
        const outdated = await getOutdatedCompliances();
        if (outdated.length > 0) {
          setOutdatedCompliances(outdated);
          setShowComplianceModal(true);
          setSubmitting(false);
          return;
        }
      } catch (error) {
        console.warn("Failed to check compliance status:", error);
      }

      if (!nameOk || !surnameOk) {
        const qs = nextSafe ? `?from=login&next=${encodeURIComponent(nextSafe)}` : `?from=login`;
        navigateTo(`/complete-profile${qs}`);
      } else {
        navigateTo(nextSafe ?? "/");
      }
    } catch (error: unknown) {
      if (error instanceof LoginAuthError) {
        if (error.code === "EMAIL_NOT_VERIFIED") {
          const qsObj: Record<string, string> = { email: error.email ?? emailTrimmed };
          if (nextSafe) qsObj.next = nextSafe;
          navigateTo(`/email-verification/pending?${new URLSearchParams(qsObj).toString()}`);
          return;
        }
        if (error.status === 401) {
          setServerError("Invalid credentials.");
          return;
        }
      }
      if (error instanceof AuthGateError && error.code === "LOGINS_DISABLED") {
        setServerError(LOGINS_DISABLED_MESSAGE);
        return;
      }
      setServerError("Something went wrong. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="auth-page">
      {/* Atmospheric orb — brand background element, bottom-right */}
      <div
        className="orb"
        aria-hidden
        style={{ position: "absolute", bottom: -80, right: -80, opacity: 0.45 }}
      />

      {/* Back to home */}
      <div className="auth-page__back">
        <button
          type="button"
          onClick={() => { navigateTo("/"); }}
          className="btn btn--ghost btn--sm"
          aria-label="Back to home"
        >
          <svg viewBox="0 0 24 24" width={16} height={16} fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <path d="M19 12H5" /><path d="m12 19-7-7 7-7" />
          </svg>
          Back
        </button>
      </div>

      {/* Intro splash overlay */}
      <AnimatePresence>
        {showIntro && (
          <motion.div
            key="intro"
            style={{
              position: "fixed",
              inset: 0,
              zIndex: 40,
              display: "grid",
              placeItems: "center",
              background: "var(--bg)",
            }}
            initial={{ opacity: 1 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={fadeQuick}
          >
            <motion.span
              className="logo logo--lg"
              initial={{ scale: 0.94, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 1.02, opacity: 0 }}
              transition={spring}
              style={{ userSelect: "none" }}
            >
              kivo<span className="dot" />
            </motion.span>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Main panel */}
      <motion.div
        style={{ width: "100%", maxWidth: 440, position: "relative", zIndex: 1 }}
        variants={cardVariants}
        initial="hidden"
        animate={showIntro ? "hidden" : "visible"}
      >
        {/* Brand row */}
        <motion.div
          variants={brandRowVariants}
          initial="hidden"
          animate={showIntro ? "hidden" : "visible"}
          className="stack-2"
          style={{ alignItems: "center", marginBottom: "var(--s-5)", userSelect: "none" }}
        >
          <span className="logo logo--lg">kivo<span className="dot" /></span>
          <span className="t-small t-mute">Feel great in your skin</span>
        </motion.div>

        {/* Auth card */}
        <motion.div variants={cardVariants} initial="hidden" animate={showIntro ? "hidden" : "visible"}>
          <div className="card stack-5">
            {/* Heading */}
            <div className="stack-2">
              <h1 className="t-h3" style={{ margin: 0 }}>Welcome <em>back</em></h1>
              <p className="t-small t-mute" style={{ margin: 0 }}>Log in to continue.</p>
            </div>

            {/* OAuth */}
            <div className="stack-3">
              <GoogleLoginButton />
              <AppleLoginButton />
              <div style={{ display: "flex", alignItems: "center", gap: "var(--s-3)", padding: "var(--s-1) 0" }}>
                <hr className="rule" style={{ flex: 1 }} />
                <span className="t-label" style={{ flexShrink: 0 }}>Or</span>
                <hr className="rule" style={{ flex: 1 }} />
              </div>
            </div>

            {/* Email + password form */}
            <form onSubmit={handleSubmit} className="stack-3" noValidate>
              <div className="stack-2">
                <input
                  className={`form-input${showEmailError ? " form-input--error" : ""}`}
                  type="email"
                  placeholder="Email"
                  value={email}
                  inputMode="email"
                  autoComplete="email"
                  pattern={EMAIL_HTML_PATTERN}
                  title="Enter a valid email like name@example.com"
                  aria-invalid={showEmailError ? "true" : "false"}
                  onChange={(e) => { setEmail(e.target.value); }}
                  onBlur={() => { setTouched((t) => ({ ...t, email: true })); }}
                  required
                />
                <AnimatePresence initial={false}>
                  {showEmailError && (
                    <motion.p
                      key="email-err"
                      className="field-error"
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={fadeQuick}
                    >
                      Please enter a valid email address.
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>

              <div className="stack-2">
                <input
                  className={`form-input${showPasswordError ? " form-input--error" : ""}`}
                  type="password"
                  placeholder="Password"
                  value={password}
                  autoComplete="current-password"
                  minLength={8}
                  maxLength={64}
                  title="8–64 characters, include upper & lower case, a number, and a symbol"
                  aria-invalid={showPasswordError ? "true" : "false"}
                  onChange={(e) => { setPassword(e.target.value); }}
                  onBlur={() => { setTouched((t) => ({ ...t, password: true })); }}
                  required
                />
                <AnimatePresence initial={false}>
                  {showPasswordError && (
                    <motion.p
                      key="pass-err"
                      className="field-error"
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={fadeQuick}
                    >
                      Use 8–64 characters with upper and lower case, a number, and a symbol.
                    </motion.p>
                  )}
                </AnimatePresence>
              </div>

              <AnimatePresence initial={false}>
                {serverError && (
                  <motion.p
                    key="server-err"
                    className="field-error"
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={fadeQuick}
                  >
                    {serverError}
                  </motion.p>
                )}
              </AnimatePresence>

              {/* Login kill-switch notice: form stays visible, submit disables */}
              {!loginEnabled && (
                <p className="t-small t-mute" role="status" aria-live="polite" style={{ margin: 0 }}>
                  {LOGINS_DISABLED_MESSAGE}
                </p>
              )}

              <div className="row-4" style={{ justifyContent: "space-between", marginTop: "var(--s-1)" }}>
                <motion.div whileTap={{ scale: prefersReduced ? 1 : 0.97 }}>
                  <button
                    type="submit"
                    className="btn btn--primary"
                    disabled={submitting || !emailValid || !passwordValid || !loginEnabled}
                  >
                    {submitting ? "Logging in…" : "Log in"}
                  </button>
                </motion.div>
                <button
                  type="button"
                  onClick={() => { navigateTo("/forgot-password"); }}
                  className="link t-small"
                >
                  Forgot password?
                </button>
              </div>
            </form>

            {/* Secondary nav */}
            <div
              className="t-small t-mute"
              style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "var(--s-3)" }}
            >
              <span>
                No account?{" "}
                <button onClick={() => { navigateTo("/register"); }} className="link">
                  Sign up
                </button>
              </span>
              <span aria-hidden>·</span>
              <button onClick={() => { navigateTo("/"); }} className="link">
                Home
              </button>
            </div>
          </div>

          {/* Legal strip */}
          <p className="t-small t-mute" style={{ margin: "var(--s-5) 0 0", textAlign: "center" }}>
            By continuing you agree to our{" "}
            <button type="button" onClick={() => { navigateTo("/terms"); }} className="link">
              Terms of Service
            </button>{" "}
            &{" "}
            <button type="button" onClick={() => { navigateTo("/privacy"); }} className="link">
              Privacy Policy
            </button>
            .
          </p>
        </motion.div>
      </motion.div>

      {/* Compliance modal */}
      {showComplianceModal && (
        <ComplianceModal
          outdatedCompliances={outdatedCompliances}
          onAllAccepted={async () => {
            setShowComplianceModal(false);
            try {
              const me = await getMe();
              const nameOk = me.name && me.name.trim() !== "";
              const surnameOk = me.surname && me.surname.trim() !== "";
              if (!nameOk || !surnameOk) {
                const qs = nextSafe ? `?from=login&next=${encodeURIComponent(nextSafe)}` : `?from=login`;
                navigateTo(`/complete-profile${qs}`);
              } else {
                navigateTo(nextSafe ?? "/");
              }
            } catch (error) {
              console.error("Failed to re-fetch user after compliance:", error);
              navigateTo("/");
            }
          }}
          onError={(msg) => { setServerError(msg); }}
        />
      )}
    </div>
  );
}
